import React, { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { AuthContext } from '../../context/AuthContext';

const TYPE_META = {
  'offering.created': { Icon: LocalOfferOutlinedIcon, label: 'Offering created' },
  'offering.accepted': { Icon: HandshakeOutlinedIcon, label: 'Offering accepted' },
  'offering.completed': { Icon: CheckCircleOutlineOutlinedIcon, label: 'Offering completed' },
  'offering.offering_available': { Icon: LocalOfferOutlinedIcon, label: 'Available offer' },
  'offering.offering_reserved': { Icon: HandshakeOutlinedIcon, label: 'Offer reserved' },
  'offering.offering_fulfilled': { Icon: CheckCircleOutlineOutlinedIcon, label: 'Offer fulfilled' },
  'offering.shared': { Icon: ShareOutlinedIcon, label: 'Offering shared' },
  'offering.joined': { Icon: PersonAddAlt1OutlinedIcon, label: 'Offering joined' },
  'task.created': { Icon: AssignmentOutlinedIcon, label: 'Task posted' },
  'task.accepted': { Icon: HandshakeOutlinedIcon, label: 'Task accepted' },
  'task.started': { Icon: PlayCircleOutlineOutlinedIcon, label: 'Task started' },
  'task.completed': { Icon: CheckCircleOutlineOutlinedIcon, label: 'Task completed' },
  'task.task_posted': { Icon: AssignmentOutlinedIcon, label: 'Task posted' },
  'task.task_accepted': { Icon: HandshakeOutlinedIcon, label: 'Task accepted' },
  'task.task_started': { Icon: PlayCircleOutlineOutlinedIcon, label: 'Task started' },
  'task.task_completed_verified': { Icon: CheckCircleOutlineOutlinedIcon, label: 'Completed & verified' },
  'task.task_pay_raised': { Icon: CampaignOutlinedIcon, label: 'Pay raised' },
  'task.helpers_renotified': { Icon: CampaignOutlinedIcon, label: 'Helpers notified again' },
  'task.task_reopened_available': { Icon: ReplayOutlinedIcon, label: 'Task available again' },
  'task.helper_released_available': { Icon: ReplayOutlinedIcon, label: 'Task available again' },
  'task.task_removed': { Icon: AssignmentOutlinedIcon, label: 'Task removed' },
  'task.shared': { Icon: ShareOutlinedIcon, label: 'Task shared' },
  'task.joined': { Icon: PersonAddAlt1OutlinedIcon, label: 'Task joined' },
  'proof.action_credit_earned': { Icon: VerifiedOutlinedIcon, label: 'Action Credit earned' },
  'localops.shared': { Icon: ShareOutlinedIcon, label: 'LocalOps shared' },
  'localops.joined': { Icon: PersonAddAlt1OutlinedIcon, label: 'LocalOps joined' },
};

const getInitial = (value) => {
  const label = String(value || '').trim();
  if (!label) return '?';
  return label.charAt(0).toUpperCase();
};

const renderAvatar = (actor, className, onClick) => {
  if (!actor) return null;
  const isClickable = typeof onClick === 'function';
  const Tag = isClickable ? 'button' : 'span';
  if (actor.profilePhoto) {
    return (
      <Tag
        type={isClickable ? 'button' : undefined}
        className={`${className}${isClickable ? ' is-clickable' : ''}`}
        onClick={(event) => {
          if (!isClickable) return;
          event.stopPropagation();
          onClick();
        }}
        onKeyDown={(eventKey) => {
          if (!isClickable) return;
          if (eventKey.key === 'Enter' || eventKey.key === ' ') {
            eventKey.preventDefault();
            onClick();
          }
        }}
        aria-label={actor.playerId ? `View ${actor.playerId}` : 'View'}
      >
        <img src={actor.profilePhoto} alt="" />
      </Tag>
    );
  }
  return (
    <Tag
      type={isClickable ? 'button' : undefined}
      className={`${className} is-fallback${isClickable ? ' is-clickable' : ''}`}
      onClick={(event) => {
        if (!isClickable) return;
        event.stopPropagation();
        onClick();
      }}
      onKeyDown={(eventKey) => {
        if (!isClickable) return;
        if (eventKey.key === 'Enter' || eventKey.key === ' ') {
          eventKey.preventDefault();
          onClick();
        }
      }}
      aria-label={actor.playerId ? `View ${actor.playerId}` : 'View'}
    >
      {getInitial(actor.playerId)}
    </Tag>
  );
};

const renderBusinessLogo = (business, className) => {
  if (!business?.logo) return null;
  return (
    <span className={`${className} localops-feed-avatar--business`}>
      <img src={business.logo} alt="" />
    </span>
  );
};

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value || '');

const normalizeSharePath = (shareUrl) => {
  const trimmed = String(shareUrl || '').trim();
  if (!trimmed) return '';
  if (isAbsoluteUrl(trimmed)) {
    try {
      const url = new URL(trimmed);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (err) {
      return '';
    }
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const getShareTarget = (shareUrl, isAuthed) => {
  const targetPath = normalizeSharePath(shareUrl);
  if (!targetPath) return null;
  if (!isAuthed) {
    return `/register?redirect=${encodeURIComponent(targetPath)}`;
  }
  return targetPath;
};

export default function LocalOpsFeedItem({
  event,
  timeAgoLabel,
  variant = 'default',
  onSelect,
}) {
  const { player } = useContext(AuthContext);
  const history = useHistory();
  const shareUrl = event?.shareUrl;
  const meta = TYPE_META[event?.type] || {};
  const Icon = meta.Icon || AssignmentOutlinedIcon;
  const typeLabel = meta.label || 'LocalOps';
  const canOpen = !!shareUrl;
  const canAvatarNavigate = canOpen && !!event?.actor;
  const summaryText = event?.summaryText || event?.label || event?.title || 'Update';
  const summaryClean = summaryText
    .replace(/^(New (offering|task)|Available offer|Local offer still available):\s*/i, '')
    .trim();
  const headline = event?.label || `${typeLabel} - ${summaryClean || summaryText}`;
  const itemClass = [
    'localops-feed-item',
    event?.isNew ? 'localops-feed-item--new' : '',
    variant === 'display' ? 'localops-feed-item--display' : '',
    canOpen ? 'is-clickable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleNavigate = useCallback(() => {
    if (!shareUrl) return;
    if (typeof onSelect === 'function' && onSelect(event)) {
      return;
    }
    const target = getShareTarget(shareUrl, !!player?.token);
    if (!target) return;
    if (isAbsoluteUrl(target)) {
      window.location.href = target;
      return;
    }
    history.push(target);
  }, [shareUrl, history, player?.token, onSelect, event]);

  if (!event) return null;

  return (
    <div
      className={itemClass}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : -1}
      onClick={() => {
        if (!canOpen) return;
        handleNavigate();
      }}
      onKeyDown={(eventKey) => {
        if (!canOpen) return;
        if (eventKey.key === 'Enter' || eventKey.key === ' ') {
          eventKey.preventDefault();
          handleNavigate();
        }
      }}
    >
      <div className="localops-feed-avatars">
        {renderAvatar(
          event.actor,
          'localops-feed-avatar',
          canAvatarNavigate ? handleNavigate : null,
        )}
        {renderBusinessLogo(event.business, 'localops-feed-avatar')}
      </div>
      <div className="localops-feed-body">
        <div className="localops-feed-summary">
          <span className="localops-feed-icon">
            <Icon fontSize="inherit" />
          </span>
          <span className="localops-feed-summary-text">{headline}</span>
        </div>
        {event.regionLabel ? (
          <div className="localops-feed-meta">near {event.regionLabel}</div>
        ) : null}
      </div>
      {timeAgoLabel ? (
        <div className="localops-feed-time">{timeAgoLabel}</div>
      ) : null}
    </div>
  );
}
